import { CompanySpaceMandatoryParams } from '../../generated-api';
import { CompanySettings, CompanySettingsPatch } from '../../store/companySettings';

export interface SettingsTabProps {
  settings: CompanySettings;
  /** only the owner may change anything but the language */
  isOwner: boolean;
  /** applies the patch at once, reverts it when the API refuses; resolves with the outcome */
  onSave: (patch: CompanySettingsPatch, toastKey?: string) => Promise<boolean>;
}

/** The API replaces the mandatory flags as a whole, so one flag is always sent with the rest */
export const mandatoryParamsPatch = (
  settings: CompanySettings,
  key: keyof CompanySpaceMandatoryParams,
  value: boolean
): CompanySettingsPatch => ({
  mandatory_params: {
    job_title: false,
    department: false,
    birth_date: false,
    personal_id: false,
    ...settings.mandatory_params,
    [key]: value,
  },
});

/**
 * The values a failed patch has to put back. Mandatory flags travel as a whole object, so only
 * the flags this patch changed are reverted -- a later save may already carry the others.
 */
export const revertedValues = (
  patch: CompanySettingsPatch,
  previous: CompanySettings,
  current: CompanySettings
): Partial<CompanySettings> => {
  const { mandatory_params } = patch;
  const reverted: Partial<CompanySettings> = Object.fromEntries(
    Object.keys(patch)
      .filter((key) => key !== 'file_id' && key !== 'mandatory_params')
      .map((key) => [key, previous[key as keyof CompanySettings]])
  );
  if (mandatory_params) {
    const flags = { ...current.mandatory_params! };
    (Object.keys(mandatory_params) as (keyof typeof mandatory_params)[]).forEach((flag) => {
      if (mandatory_params[flag] !== previous.mandatory_params?.[flag]) {
        flags[flag] = !!previous.mandatory_params?.[flag];
      }
    });
    reverted.mandatory_params = flags;
  }
  return reverted;
};
